import { useState, useCallback, useEffect, useRef } from 'react';
import {
  GridLogicOperator,
  type GridPaginationModel,
  type GridSortModel,
  type GridFilterModel
} from '@mui/x-data-grid-premium';

export const useWorkOrderFilters = () => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'expectedDueDate',
      sort: 'asc'
    }
  ]);

  const [searchText, setSearchText] = useState<string>('');
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [
      {
        field: 'status',
        operator: 'equals',
        value: 'INCOMPLETE',
        id: 1
      }
    ],
    logicOperator: GridLogicOperator.And
  });

  useEffect(() => {
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchText]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);

  const handleColumnFiltersChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      const itemsWithIds = newFilterModel.items.map((item, index) => ({
        ...item,
        id: item.id || index + 1
      }));

      const updatedFilterModel = {
        ...newFilterModel,
        items: itemsWithIds,
        logicOperator: newFilterModel.logicOperator || GridLogicOperator.And
      };

      setFilterModel(updatedFilterModel);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    },
    []
  );

  const resetFilters = useCallback(() => {
    setSearchText('');
    setDebouncedSearchText('');
    setFilterModel({
      items: [
        {
          field: 'status',
          operator: 'equals',
          value: 'INCOMPLETE',
          id: 1
        }
      ],
      logicOperator: GridLogicOperator.And
    });
    setSortModel([
      {
        field: 'expectedDueDate',
        sort: 'asc'
      }
    ]);
    setPaginationModel({ page: 0, pageSize: 10 });
  }, []);

  return {
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    searchText: debouncedSearchText,
    inputSearchText: searchText,
    setSearchText,
    filterModel,
    setFilterModel,
    handleSearchChange,
    handleColumnFiltersChange,
    resetFilters
  };
};
